import H from "highcharts";
import Accessibility from "highcharts/modules/accessibility";
import Exporting from "highcharts/modules/exporting";
import offlineExporting from "highcharts/modules/offline-exporting";
import Stock from 'highcharts/modules/stock';
import { translate } from "../../data-views/utils"
Stock(H);
Accessibility(H);
Exporting(H); // Required eventhough using offlineExporting
offlineExporting(H);

import {
  ChartType,
  ExportType,
  type ChartConfig,
  type NumberFormatter,
  type TimeSeries,
  type TimeSeriesChartInterface,
  type UpdateTimeSeries,
} from "../../data-views/chart-interfaces";
import config from "./highcharts-time-series-config.json";

type MonthMap = {
  [key: string]: number;
};

export class TimeSeriesChart implements TimeSeriesChartInterface {
  private chart!: H.Chart;
  private config: H.Options;
  private numberFormatter: NumberFormatter = (n) => String(n);
  private rendererTexts: H.SVGElement[] = [];

  /**
   * Map chart interface types to Highcharts types
   */
  private static readonly chartTypeMap = {
    [ChartType.LINE]: "line" as H.SeriesLineOptions["type"],
    [ChartType.BAR]: "column" as H.SeriesColumnOptions["type"],
  };

  private static readonly monthMap : MonthMap = {
    January: 0,
    February: 1,
    March: 2,
    April: 3,
    May: 4,
    June: 5,
    July: 6,
    August: 7,
    September: 8,
    October: 9,
    November: 10,
    December: 11
  };

  public constructor() {
    this.config = config as H.Options;
    H.setOptions({
      lang: {
        rangeSelectorFrom: translate("From"),
        rangeSelectorTo: translate("To"),
        rangeSelectorZoom: "Zoom",
        thousandsSep: ",",
        decimalPoint: ".",
        resetZoom: "Reset zoom",
        resetZoomTitle: "Reset zoom level 1:1",
        shortMonths: [
          translate("Jan"),
          translate("Feb"),
          translate("Mar"),
          translate("Apr"),
          translate("May"),
          translate("Jun"),
          translate("Jul"),
          translate("Aug"),
          translate("Sep"),
          translate("Oct"),
          translate("Nov"),
          translate("Dec")
        ],
        shortWeekdays: [
          translate("Mon"),
          translate("Tue"),
          translate("Wed"),
          translate("Thu"),
          translate("Fri"),
          translate("Sat"), 
          translate("Sun")
        ],
      }
  });

  }

  

  /**
   * Init the chart once the container is available (html has been rendered)
   *
   * @param renderTo - The container to render the chart to
   * @param numberFormatter - A function to format numbers
   */
  public init(
    renderTo: HTMLDivElement,
    numberFormatter?: NumberFormatter
  ): void {
    if (numberFormatter !== undefined) {
      this.numberFormatter = numberFormatter;
    }
    this.config = this.setRenderTo(renderTo, this.config);
    this.chart = H.chart(this.config);
    this.registerEvents();
  }

  /**
   * Set renderTo attribute in the config
   *
   * @param renderTo - HTML element to render the chart to
   * @param config - Chart configuration
   * @returns Chart configuration with renderTo attribute set
   */
  private setRenderTo(renderTo: HTMLDivElement, config: H.Options): H.Options {
    config.chart = config.chart || {};
    config.chart.renderTo = renderTo;
    return config;
  }

  /**
   * Register events for the chart
   */
  private registerEvents(): void {
    const timeSeriesChart = this; // Reference to this for event handlers

    this.chart.update({
      chart: {
        events: {
          // Update renderer texts everytime the chart is rendered (this applies also to exported charts)
          render: function () {
            timeSeriesChart.updateSources(this);
          },
          beforePrint: function () {
            this.update({rangeSelector: {enabled: false}})
            const zoomButton = timeSeriesChart.getZoomButton();
            if (zoomButton !== null) zoomButton.style.visibility = "hidden";
          },
          afterPrint: function () {
            this.update({rangeSelector: {enabled: true}})
            const zoomButton = timeSeriesChart.getZoomButton();
            if (zoomButton !== null) zoomButton.style.visibility = "visible";
            this.redraw();
          },
        },
      },
    });
  }

  /**
   * Add series to the chart
   *
   * @param timeSeries - An array of time series
   * @param chartConfig - Additional chart configuration to update axis etc.
   */
  public addTimeSeries(
    timeSeries: TimeSeries[],
    chartConfig?: ChartConfig
  ): void {
    timeSeries.forEach((ts) => {
      if (this.chart.get(ts.id) !== undefined) {
        return;
      }
      
      const series = this.chart.addSeries(
        {
          index: this.createIndex(this.chart),
          id: ts.id,
          name: ts.name,
          type: TimeSeriesChart.chartTypeMap[ts.type],
          data: this.timeSeriesDataToChartData(ts.data),
          custom: {
            timeSeries: ts,
          },
        },
        false // Wait for all updates to be done before redrawing
      );

      if (chartConfig?.data?.showPrevious) {
        this.addPreviousValues([series]);
      }
    });

    this.updateChart(chartConfig);
  }

  /**
   * Update chart's series
   *
   * @param timeSeries - An array of time series
   * @param chartConfig - Additional chart configuration to update axis etc.
   */
  public updateTimeSeries(
    timeSeries: UpdateTimeSeries[],
    chartConfig?: ChartConfig
  ): void {
    timeSeries.forEach((ts) => {
      const series = this.chart.get(ts.id || ""); // || "" for runtime check
      if (series === undefined) {
        return;
      }

      const updates: { [key: string]: any } = {
        custom: {
          timeSeries: ts,
        },
      };

      if (ts.name !== undefined) updates["name"] = ts.name;

      if (ts.type !== undefined)
        updates["type"] = TimeSeriesChart.chartTypeMap[ts.type];

      if (ts.data !== undefined)
        updates["data"] = this.timeSeriesDataToChartData(ts.data);

      series.update(
        updates as never,
        false // Wait for all updates to be done before redrawing
      );
    });

    this.removeLinkedSeries(this.chart.series);
    if (chartConfig?.data?.showPrevious) {
      this.addPreviousValues(this.chart.series);
    }

    this.updateChart(chartConfig);
  }

  /**
   * Convert time series data to chart data
   *
   * @param data - Array of TimeSeries data points
   * @returns Array of chart data
   */
  private timeSeriesDataToChartData(
    data: TimeSeries["data"]
  ): Partial<H.Point>[] {
    const trimmedData = this.trimNulls(data);
    return trimmedData.map((d) => ({
      x: d.date.getTime(),
      y: d.value !== null ? d.value : undefined,
      custom: {
        valueLabel: d.valueLabel,
        dateLabel: d.dateLabel,
        source: d.source,
      },
    }));
  }

  /**
   * Trim nulls from the start and end of the series
   *
   * @param data - Array of TimeSeries data points
   *
   */
  private trimNulls(data: TimeSeries["data"]): TimeSeries["data"] {
    let start = 0;
    let end = data.length;

    while (data[start]?.value === null) start++;
    while (data[end - 1]?.value === null) end--;

    return data.slice(start, end);
  }

  /**
   * Remove series from the chart
   *
   * @param ids - Array of series ids to remove
   * @param chartConfig - Additional chart configuration to update axis etc.
   */
  public removeTimeSeries(ids: string[], chartConfig?: ChartConfig): void {
    ids.forEach((id) => {
      const series = this.chart.get(id) as
        | (H.Series & { linkedSeries?: H.Series[] }) // Series that are linked have linkedSeries property
        | undefined;

      if (series === undefined) {
        return;
      }

      const linkedSeries = series.linkedSeries || [];
      this.removeLinkedSeries(linkedSeries);

      series.remove(false); // Wait for all updates to be done before redrawing
    });

    this.updateChart(chartConfig);
  }

  /**
   * Get time series that were used to create the chart
   *
   * @returns Array of time series
   */
  public getTimeSeries(): TimeSeries[] {
    return this.chart.series.reduce((acc, s) => {
      const timeSeries = s.options.custom?.timeSeries as TimeSeries | undefined;
      if (timeSeries !== undefined) {
        acc.push(timeSeries);
      }
      return acc;
    }, [] as TimeSeries[]);
  }

  /**
   * Export chart
   *
   * @param type - MIME type of the export
   */
  public export(type: ExportType): void {
    this.chart.exportChartLocal(
      { type },
      {
        chart: {
          backgroundColor: "#ffffff",
        },
      }
    );
  }

  public print(): void {
    this.chart.print();
  }

  /**
   * Update chart configuration (axis, tooltip etc.)
   *
   * @param chartConfig - Chart configuration for updates.
   */
  private updateChart(chartConfig?: ChartConfig): void {
    this.updateXAxis(chartConfig?.xAxis);
    this.updateYAxis(chartConfig?.yAxis);
    this.updateTooltip();
    this.updateChartTitle(chartConfig?.chart);
    this.chart.reflow();
    this.chart.redraw();
  }

  /**
   * Update tooltips based on the series data
   */
  private updateTooltip(): void {
    this.chart.tooltip.options.formatter = function (): string {
      const name = this.series.name;
  
      // Sanitizing tooltip content
      let statName = name.split("(")[0].trim();
  
      let options: any = [];
  
      const regex = /\((.*)\)$/; // Matches everything inside the outermost parentheses
      const match = name.match(regex);
      const extractedValue = match ? match[1] : '';
  
      // Split the extracted value by commas without removing spaces
      extractedValue.split(',').forEach((value) => {
        options.push(value.split(":")[1].trim());
      });
  
      let final_name = statName + " (" + options.join(', ') + ")";
      
      const date =
        this.point.options.custom?.dateLabel ||
        H.dateFormat("%b %Y", this.x as number);
  
      const value =
        this.point.options.custom?.valueLabel ||
        H.numberFormat(this.y as number, 0);
  
      return `<b>${final_name}</b><br>${date}: ${value}`;
    };
  }

  /**
   * Update X axis
   *
   * @param xAxisConfig - X axis configuration from the chartConfig
   */
  private updateXAxis(xAxisConfig?: ChartConfig["xAxis"]): void {
    const alignTicksWithData = this.alignTicksWithData;

    const firstSeriesData = (
      this.chart.series?.[0]?.userOptions as {
        data: Partial<H.Point>[];
      }
    )?.data;

    const dataIntervalInDays = this.getDataIntervalInDays(firstSeriesData);
    const dataIntervalInMs = this.daysToMilliseconds(dataIntervalInDays);

    let monthFrom;
    let monthTo;
    
    if (xAxisConfig?.aggregatedToMonth.split(" ")[0] !== undefined && xAxisConfig?.aggregatedToMonth.split(" ")[1] !== undefined){
      monthFrom = TimeSeriesChart.monthMap[xAxisConfig?.aggregatedToMonth.split(" ")[0]];
      monthTo = TimeSeriesChart.monthMap[xAxisConfig?.aggregatedToMonth.split(" ")[1]];
    } else if (xAxisConfig?.aggregatedToMonth === "Years"){
      monthFrom = 0;
      monthTo = 11;
    } else {
      monthFrom = this.getFirstDatapointMonth(firstSeriesData);
    }

    const yearTimeLabelFormat =
      monthFrom === undefined
        ? ""
        : this.createYearTimeLabelFormat(monthFrom, monthTo);

    const updates: { [key: string]: any } = {
      minRange: dataIntervalInMs,
      minTickInterval: dataIntervalInMs,

      tickPositioner: [365, 366].includes(dataIntervalInDays)
        ? function (this: H.Axis & H.Series): H.AxisTickPositionsArray {
            return alignTicksWithData(
              this.tickPositions || [],
              this.dataMin || 0
            );
          }
        : undefined,

      dateTimeLabelFormats: {
        month: "%b %Y",
        year: yearTimeLabelFormat,
      },
    };

    xAxisConfig?.title !== undefined &&
      (updates["title"] = { text: xAxisConfig.title });

    this.chart.xAxis[0].update(updates, false);
  }

  /**
   * Update Y axis
   *
   * @param yAxisConfig - Y axis configuration from the chartConfig
   */
  private updateYAxis(yAxisConfig: ChartConfig["yAxis"]): void {
    const numberFormatter = this.numberFormatter;
    const isPercent = yAxisConfig?.isPercent || false;
    const isLineChart =
      this.chart.series?.[0]?.userOptions?.type === "line" || false;
    const min = this.getMinimumValue(this.chart.series);

    const updates: { [key: string]: any } = {
      min: min < 0 ? null : 0, // min is zero when all values are positive
      labels: {
        formatter: function (): string {
          return numberFormatter(this.value as number, isPercent);
        },
      },
      plotLines: [
        {
          color: "#f15d5c",
          width: isLineChart ? 1 : 0,
          value: 0,
          dashStyle: "dash",
          zIndex: 1,
        },
      ],
    };

    yAxisConfig?.title !== undefined &&
      (updates["title"] = { text: yAxisConfig.title });

    this.chart.yAxis[0].update(updates, false);
  }

  /**
   * Update sources
   *
   * Reference to the created SVG elements is stored in rendererTexts to allow
   * the removal of the elements when the chart is updated.
   */
  private updateSources(chart: H.Chart): void {
    const isExport = this.chart !== chart; // Exported chart creates a new chart instance

    if (!isExport) {
      // Do not remove sources on this instance when exporting
      this.removeRendererTexts();
    }

    const uniqueSources = this.getUniqueSources(this.chart.series).join(", ");
    if (!uniqueSources) return;

    const sourcesText = isExport
      ? `Visitory.io (${translate('sources')}: ${uniqueSources})`
      : `${translate('Sources')}: `+ uniqueSources;

    const text = this.renderSources(chart, sourcesText);

    if (!isExport) {
      // Do not add sources to this instance when exporting
      this.rendererTexts.push(text);
    }
  }

  /**
   * Update chart (title, subtitle, etc.)
   *
   * @param chartConfig - Chart configuration from the chartConfig
   */
  private updateChartTitle(chartConfig: ChartConfig["chart"]): void {
    const title = chartConfig?.title;
    const subtitle = chartConfig?.subtitle;
    this.chart.setTitle({ text: title }, { text: subtitle }, false);
  }

  /**
   * Render sources text to chart
   *
   * @param chart - Highcharts chart
   * @param sources - Sources to render
   * @returns text renderer element
   */
  private renderSources(chart: H.Chart, sources: string): H.SVGElement {
    return chart.renderer
      .text(sources, chart.plotLeft, chart.chartHeight - 5)
      .css({ color: "#666", fontSize: "smaller" })
      .add();
  }

  /**
   * Remove all texts created by the chart.renderer
   */
  private removeRendererTexts(): void {
    this.rendererTexts.forEach((text) => text.destroy());
    this.rendererTexts = [];
  }

  private getZoomButton(): HTMLButtonElement | null {
    return document.querySelector(".highcharts-reset-zoom");
  }

  /**
   * Create index for new series.
   * The index is spread to leave space for adding series in between.
   *
   * @param chart - Highcharts chart
   * @returns index for series data
   */
  private createIndex(chart: H.Chart): number {
    const currentMaxIndex = chart.series.at(-1)?.userOptions.index || 0;
    return currentMaxIndex + 10;
  }

  /**
   * Get unique sources from the series
   *
   * @param series - Array of series
   * @returns List of unique sources
   */
  private getUniqueSources(series: H.Series[]) {
    return series.reduce((sources: string[], s) => {
      const data = (s.userOptions as { data: Partial<H.PointOptionsObject>[] })
        .data;

      const newSources = data.reduce((sources: string[], d) => {
        const source = d?.custom?.source;
        if (source !== undefined && !sources.includes(source)) {
          sources.push(source);
        }
        return sources;
      }, []);

      return [...new Set([...sources, ...newSources])];
    }, []);
  }

  /**
   * Get the minimum value across all series
   *
   * @param series - An array of series
   * @returns minimum value
   */
  private getMinimumValue(series: H.Series[]): number {
    return series.reduce(
      (min, s) =>
        Math.min(
          min,
          ...(s.userOptions as { data: Partial<H.Point>[] }).data.map((d) => {
            return d?.y || Infinity;
          })
        ),
      Infinity
    );
  }

  /**
   * Calculate previous values for a series
   *
   * @param data - Array of data points
   * @returns Array of data points with previous year's values
   */
  private calculatePreviousValues(
    data: Partial<H.Point>[]
  ): Partial<H.Point>[] {
    const interval = this.getDataIntervalInDays(data);

    const step = [365, 366].includes(interval)
      ? 1
      : [28, 29, 30, 31].includes(interval)
      ? 12
      : interval == 1
      ? 7 * 52 // Match same weekday a year ago
      : Infinity;

    return data.reduce((acc, d, index) => {
      // Discard first year's values as they have no previous year's data available
      if (index >= step) {
        acc.push({
          ...data[index - step], // Copy everything from the previous year except the x value
          x: d.x,
        });
      }

      return acc;
    }, [] as Partial<H.Point>[]);
  }

  /**
   * Align ticks with data by shifting automatically calculated tick
   * positions based on the first item.
   *
   * By default the ticks are placed on January.
   *
   * @param tickPositions - Automatically calculated tick positions
   * @param dataMin - First data point in the series
   * @returns Shifted - tick positions
   */
  private alignTicksWithData(
    tickPositions: H.AxisTickPositionsArray,
    dataMin: number
  ): H.AxisTickPositionsArray {
    return tickPositions.map((tickPosition) =>
      Date.UTC(
        new Date(tickPosition).getFullYear(),
        new Date(dataMin).getMonth()
      )
    );
  }

  /**
   * Create time label format for yearly data (interval between datapoints is one year)
   *
   * @param monthFrom - Start month for the time label format
   * @param monthTo - End month for the time label format
   * @returns Time label format
   */
  private createYearTimeLabelFormat(
    monthFrom: number,
    monthTo?: number
  ): string {
    if (monthTo === undefined) {
      return "%b %Y"; // Assumption: data is not aggregated
    }
    if (monthFrom === monthTo) {
      return "%b %Y";
    }
    if (monthFrom == 0 && monthTo == 11) {
      return "%Y";
    }
    return `${monthFrom + 1}-${monthTo + 1} %Y`;
  }

  /**
   * Get the month (as number) of the first data point
   *
   * @param data - Array of data points
   * @returns The month of the first datapoint or undefined if no data
   */
  private getFirstDatapointMonth(data: Partial<H.Point>[]): number | undefined {
    const date = data?.[0]?.x;
    return date ? new Date(date).getMonth() : undefined;
  }

  /**
   * Get the interval between two data points in days
   *
   * @param data - Array of data points
   * @returns The interval in days
   */
  private getDataIntervalInDays(data: Partial<H.Point>[]): number {
    const firstDate = data?.[0]?.x || 0;
    const secondDate = data?.[1]?.x || 0;
    const diff = secondDate - firstDate;
    return diff / (1000 * 60 * 60 * 24);
  }

  /**
   * Convert days to milliseconds
   *
   * @param days - Number of days
   * @returns Number of milliseconds
   */
  private daysToMilliseconds(days: number): number {
    return days * 24 * 60 * 60 * 1000;
  }

  /**
   * Add previous values to the chart
   *
   * @param series - Array of series
   */
  private addPreviousValues(
    series: (H.Series & { color?: any })[] // There actually is a color property, but it's not in the types
  ): void {
    // Iterate over copy as we're modifying the series
    [...series].forEach((s) => {
      const data = (
        s.userOptions as H.SeriesOptionsType & { data: Partial<H.Point>[] }
      ).data;

      const prevYearData = this.calculatePreviousValues(data);
      if (prevYearData.length == 0) return;

      this.chart.addSeries(
        {
          ...s.userOptions,
          index: (s.userOptions.index || 0) - 1,
          id: s.userOptions.id + "_prev",
          linkedTo: s.userOptions.id,
          color: new H.Color(s.color || "#000000").setOpacity(0.5).get(),
          data: prevYearData,
          dashStyle: "Dash",
          marker: { enabled: false },
          custom: {
            timeSeries: undefined,
          },
        } as H.SeriesOptionsType,
        false // Wait for all updates to be done before redrawing
      );
    });
  }

  /**
   * Remove all linked series from the chart
   *
   * @param series - Array of series
   */
  private removeLinkedSeries(
    // Linked series actually have a linkedParent property, but it's not in the types
    series: (H.Series & { linkedParent?: {} })[]
  ): void {
    for (let i = series.length - 1; i >= 0; i--) {
      if (series[i].linkedParent) {
        series[i].remove(false);
      }
    }
  }
}
